import {withHistoryUpdate} from "@buildwithflux/core";

import {applySetDocumentDataFromSnapshot} from "../actions/documentDataActions";
import {applyApplyPreview, applyClearPreview, applyRenderPreview} from "../actions/previewActions";
import {buildDocumentStateUpdate, performDocumentMutation} from "../documentMutations";
import {notifyServicesOfAction} from "../serviceNotifier";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type DocumentDataSliceState = Pick<
    DocumentStoreState,
    | "setDocumentData"
    | "mergeDocumentData"
    | "clearDocumentData"
    | "setDocumentDataFromSnapshot"
    | "renderPreview"
    | "clearPreview"
    | "applyPreviewChanges"
>;

export function createDocumentDataSlice(deps: SliceDependencies): DocumentDataSliceState {
    const {primitives, set, get, currentUserService, services, actionEventBus} = deps;

    return {
        setDocumentData: (documentData) => {
            if (!documentData) {
                return;
            }
            set(buildDocumentStateUpdate(documentData), false, "document/setDocumentData");
            notifyServicesOfAction(
                {
                    type: "setDocumentData",
                    payload: {
                        documentData,
                        canUndo: false,
                        updatesDocumentTimestamp: false,
                        shouldGenerateActionRecord: false,
                    },
                },
                services,
                actionEventBus,
            );
        },

        mergeDocumentData: (partialDocumentData) => {
            const document = get().document;
            if (!document) {
                return;
            }
            const result = {...document, ...partialDocumentData};
            set(buildDocumentStateUpdate(result), false, "document/mergeDocumentData");
            notifyServicesOfAction(
                {
                    type: "mergeDocumentData",
                    payload: {
                        partialDocumentData,
                        canUndo: false,
                        updatesDocumentTimestamp: false,
                        shouldGenerateActionRecord: false,
                    },
                },
                services,
                actionEventBus,
            );
        },

        clearDocumentData: () => {
            set(buildDocumentStateUpdate(null, get().document), false, "document/clearDocumentData");
            notifyServicesOfAction(
                {
                    type: "clearDocumentData",
                    payload: {
                        canUndo: false,
                        updatesDocumentTimestamp: false,
                        shouldGenerateActionRecord: false,
                    },
                },
                services,
                actionEventBus,
            );
        },

        setDocumentDataFromSnapshot: (documentData) => {
            if (!documentData) {
                return;
            }
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetDocumentDataFromSnapshot(draft, documentData), {
                actionType: "setDocumentDataFromSnapshot",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
            });
        },

        renderPreview: (preview, subscriptionKey) => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyRenderPreview(draft, document, preview), {
                actionType: "renderPreview",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                    ...(subscriptionKey ? {subscriptionKey} : undefined),
                },
            });
        },

        clearPreview: (subscriptionKey) => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyClearPreview(draft, document), {
                actionType: "clearPreview",
                canUndo: false,
                shouldGenerateActionRecord: false,
                updatesDocumentTimestamp: false,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                    ...(subscriptionKey ? {subscriptionKey} : undefined),
                },
            });
        },

        applyPreviewChanges: (previewDiff, subscriptionKey) => {
            if (!previewDiff) {
                return;
            }
            const document = get().document;
            if (!document) {
                return;
            }
            const newDocument = withHistoryUpdate(document, (draft) => applyApplyPreview(draft, document, previewDiff));
            if (newDocument !== document) {
                set(buildDocumentStateUpdate(newDocument), false, "applyPreview");
            }
            notifyServicesOfAction(
                {
                    type: "applyPreview",
                    currentUser: currentUserService?.getCurrentUser(),
                    payload: {
                        canUndo: true,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                        ...(subscriptionKey ? {subscriptionKey} : undefined),
                    },
                },
                services,
                actionEventBus,
            );
        },
    };
}
